import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import matchMediaDesktop from '../../../utils/client_util';

export default class EditorHightlights extends Component {
  constructor(props) {
    super(props);
    this.state = {
      active: false,
      highlights: [],
      unread: 100
    };
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    this.isDesktop = matchMediaDesktop('1025px');
    if (this.isDesktop) {
      this.setHighlights();
    }
  }

  shouldComponentUpdate({ activePostId }, { highlights, active, unread }) {
    return (
      (activePostId !== this.props.activePostId ||
        !isEqual(highlights, this.state.highlights) ||
        active !== this.state.active ||
        unread !== this.state.unread) &&
      this.isDesktop
    );
  }

  componentDidUpdate({ activePostId }) {
    if (this.isDesktop) {
      if (activePostId !== this.props.activePostId) {
        this.removeListeners();
      }
      this.setHighlights();
    }
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  getPostContainer() {
    return document.querySelector(`#post__wrapper-${this.props.activePostId}`);
  }

  setHighlights() {
    this.postContainer = this.getPostContainer();
    const highlights = this.postContainer
      ? this.postContainer.querySelectorAll('mark')
      : [];
    const hasHighlights = Boolean(highlights.length);

    this.setHighlightsStates(highlights);

    if (hasHighlights) {
      window.addEventListener('scroll', this.handleScroll);
    }
  }

  setVisibleState(visible) {
    this.setState({
      visible
    });
  }

  setHighlightsStates(highlights) {
    this.setState({
      highlights: [...highlights]
    });
  }

  getHighlightStyles(index) {
    const parentHeight = this.postContainer.offsetHeight;
    const parentOffset = this.postContainer.offsetTop;
    const highlightOffset = this.state.highlights[index].offsetTop;
    const transformDist = 100;
    const transformPercent = highlightOffset / parentHeight * 100;
    const transformLocation =
      this.state.unread - transformPercent - transformDist / 2;

    return {
      top: `${(highlightOffset - parentOffset) / parentHeight * 100}%`,
      transform: `translateY(${transformLocation}px)`
    };
  }

  getUnreadPercent() {
    let percent = 100;
    const parentRect = this.postContainer.getBoundingClientRect();

    if (parentRect.top < 0) {
      const currentPercent =
        (parentRect.height + parentRect.top) / parentRect.height * 100;
      percent = currentPercent >= 0 ? currentPercent : 0;
    }

    return percent;
  }

  setActiveHighlightStyles(index) {
    const styles = {};
    const { highlights, active } = this.state;
    const highlight = highlights[active];
    if (index === active && highlight) {
      const totalChars = highlight.innerText.split('').length;
      const defaultHeight = 60;
      const height = totalChars / 65 * 30;
      styles.height = `${height + defaultHeight}px`;
    }
    return styles;
  }

  handleScroll() {
    const top = 200;
    const bottom = window.innerHeight;
    this.state.highlights.forEach((highlight, index) => {
      const rect = highlight.getBoundingClientRect();
      if (
        this.state.active !== index &&
        rect.top > top &&
        rect.bottom < bottom
      ) {
        this.setState({
          active: index
        });
        highlight.classList.add('active');
      } else if (
        this.state.active === index &&
        (rect.top < top || rect.bottom > bottom)
      ) {
        this.setState({
          active: undefined
        });
        highlight.classList.add('set');
        highlight.classList.remove('active');
      }
    });

    const unreadPercent = this.getUnreadPercent();
    if (unreadPercent !== this.state.unread) {
      this.setState({
        unread: unreadPercent
      });
    }

    const headerTop = this.postContainer
      .querySelector('.post__header')
      .getBoundingClientRect().top;
    if (this.state.visible && (this.state.unread < 15 || headerTop > 135)) {
      this.setVisibleState(false);
    } else if (
      !this.state.visible &&
      this.state.unread >= 15 &&
      headerTop <= 135
    ) {
      this.setVisibleState(true);
    }
  }

  handleScrollToHighlight(index) {
    const hightlight = this.state.highlights[index];
    if (hightlight) {
      window.scroll({
        top:
          hightlight.offsetTop +
          hightlight.offsetParent.offsetTop -
          window.innerHeight / 2 -
          hightlight.offsetHeight / 2,
        left: 0,
        behavior: 'smooth'
      });
    }
  }

  removeListeners() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  render() {
    if (!this.props.activePostId) return null;

    return (
      <div className="editor-highlights">
        {this.state.highlights.map((_, index) => (
          <div
            className={`editor-highlights__highlight ${
              index === this.state.active
                ? 'editor-highlights__highlight--active'
                : ''
            } ${
              this.state.visible ? 'editor-highlights__highlight--visible' : ''
            }`}
            style={this.getHighlightStyles(index)}
            onClick={() => this.handleScrollToHighlight(index)}
          >
            <span className="hotspot" />
            <span
              className="expander"
              style={this.setActiveHighlightStyles(index)}
            />
          </div>
        ))}
        <div
          className={`editor-highlights__unread ${
            this.state.visible ? 'editor-highlights__unread--visible' : ''
          }`}
          style={{ height: `${this.state.unread}%` }}
        />
      </div>
    );
  }
}

EditorHightlights.propTypes = {
  activePostId: PropTypes.string.isRequired
};
